import Phaser from 'phaser';
import { TABLE_BG, availableDenominations } from '../../config.js';
import { landscapeLayout, portraitLayout } from '../../layout/tableLayout.js';
import { ATS_NUMBERS, atsStripLayout, buildPassLineZone, buildZonesFromSvg } from '../../layout/zones.js';
import { AtsBetZone } from '../../objects/AtsBetZone.js';
import { BetZone } from '../../objects/BetZone.js';
import { ShimmerClock } from '../../objects/ShimmerClock.js';
import { Bezel } from '../../components/Bezel.js';
import { ChipRack } from '../../objects/ChipRack.js';
import { Dice } from '../../objects/Dice.js';
import { DiceThrow, type TweenAdapter } from '../../objects/DiceThrow.js';
import { createSimulator } from '../../objects/throwSimulator.js';
import { DEPTH, GESTURE, PHYSICS } from '../../objects/diceThrowConfig.js';
import { Puck } from '../../objects/Puck.js';
import * as feel from '../../feel.js';

export class TableScene extends Phaser.Scene {
  zones: BetZone[] = [];
  atsZones: AtsBetZone[] = [];
  shimmerClock?: ShimmerClock | undefined;
  dice1!: Dice;
  dice2!: Dice;
  puck!: Puck;
  /** Resting position the puck slides back to when the point goes OFF.
   *  Captured from the layout at create-time so the bridge can pass it to
   *  Puck.setOff without reaching back into layout state. */
  puckOffAnchor!: { x: number; y: number };
  rack!: ChipRack;
  private diceThrow!: DiceThrow;
  // Default OFF so the dice can't be thrown before the bridge has had a
  // chance to call refreshRollGate (which only enables rolling once a Pass
  // / Don't Pass bet is on the layout).
  private rollEnabled = false;
  private helpMode = false;
  private helpTint: Phaser.GameObjects.Rectangle | undefined;
  private helpBanner: Phaser.GameObjects.Text | undefined;

  constructor() {
    super('CrapsTableScene');
  }

  /**
   * Phaser preserves the scene class instance across stop/start; class-field
   * references can point to destroyed GameObjects from the prior run, but
   * stay JS-truthy. Clear them in init() so external code (e.g. the bridge
   * polling for "scene ready") can't latch onto a stale reference before
   * this run's create() reassigns it.
   */
  init() {
    this.shimmerClock = undefined;
  }

  create() {
    // Class fields only initialize ONCE per scene instance. When the scene
    // restarts (back-to-floor → re-enter), this.zones still references the
    // destroyed BetZones from the prior run; pushing new ones on top yields
    // a half-stale array whose dead zones throw on .scene.textures lookups.
    this.zones = [];
    this.atsZones = [];
    this.cameras.main.setBackgroundColor(TABLE_BG);
    this.input.mouse?.disableContextMenu();

    const W = this.scale.width;
    const H = this.scale.height;
    const layout = H > W ? portraitLayout(W, H) : landscapeLayout(W, H);

    // Render panels in order — main first, hardway on top so the felt-strip
    // overlap renders correctly (only hardway has content there).
    for (const panel of layout.panels) {
      const img = this.add.image(panel.dst.x, panel.dst.y, panel.svgKey);
      img.setOrigin(0, 0);
      img.setDisplaySize(panel.dst.w, panel.dst.h);
      img.setDepth(-10);
    }

    this.shimmerClock = new ShimmerClock(this);

    // Build zones for each panel from its source SVG.
    for (const panel of layout.panels) {
      const textKey = panel.svgKey === 'tableMain' ? 'tableMainSvgText' : 'tableHardwaySvgText';
      const svgText = this.cache.text.get(textKey) as string;
      const defs = buildZonesFromSvg(svgText, panel.dst);
      for (const def of defs) {
        const zone = new BetZone(this, def, {
          onClick: (z) => this.events.emit(this.helpMode ? 'zone:explain' : 'zone:click', z.def),
          onLongPress: (z) => {
            if (this.helpMode) return;
            this.events.emit('zone:longPress', z.def);
          },
          onHover: (z) => this.events.emit('zone:hover', z.def, z.hoverAnchor()),
          onHoverEnd: (z) => this.events.emit('zone:hoverEnd', z.def),
        }, this.shimmerClock);
        this.zones.push(zone);
      }
      // Pass line is procedural and only lives on the main panel.
      if (panel.svgKey === 'tableMain') {
        const def = buildPassLineZone(panel.dst, panel.viewBox);
        const zone = new BetZone(this, def, {
          onClick: (z) => this.events.emit(this.helpMode ? 'zone:explain' : 'zone:click', z.def),
          onLongPress: (z) => {
            if (this.helpMode) return;
            this.events.emit('zone:longPress', z.def);
          },
          onHover: (z) => this.events.emit('zone:hover', z.def, z.hoverAnchor()),
          onHoverEnd: (z) => this.events.emit('zone:hoverEnd', z.def),
        }, this.shimmerClock);
        this.zones.push(zone);
      }
    }

    // ATS strip (Small / All / Tall) — placed by the layout factory:
    // portrait stacks the cells vertically right of the hardway, landscape
    // tucks them horizontally into the empty slab above the hardway. Cell
    // rects + a shared number font size come from atsStripLayout, which
    // branches on the strip's aspect ratio.
    this.atsZones = [];
    if (layout.atsStripRect) {
      const dpr = (this.game.registry.get('dpr') as number | undefined) ?? 1;
      const { cells, numberFontSize } = atsStripLayout(layout.atsStripRect, dpr);
      for (const cell of cells) {
        const numbers = ATS_NUMBERS[cell.id];
        const zone = new AtsBetZone({
          scene: this,
          rect: cell.rect,
          def: {
            id: cell.id,
            type: cell.id,
            label: cell.label,
            polygon: [],
            payoutHint: '',
          },
          numbers: numbers.numbers,
          twoRows: numbers.twoRows,
          numberFontSize,
          shimmerClock: this.shimmerClock,
          events: {
            onClick: (z) => this.events.emit(this.helpMode ? 'zone:explain' : 'zone:click', z.def),
            onLongPress: (z) => {
              if (this.helpMode) return;
              this.events.emit('zone:longPress', z.def);
            },
            onHover: (z) => this.events.emit('zone:hover', z.def, z.hoverAnchor()),
            onHoverEnd: (z) => this.events.emit('zone:hoverEnd', z.def),
          },
        });
        this.atsZones.push(zone);
      }
    }

    // DEBUG: stroke every hit area so we can see whether they line up with
    // the visible felt art. Toggle with ?debug=zones in the URL.
    const wantDebug =
      typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debug');
    if (wantDebug) {
      const dbg = this.add.graphics();
      dbg.setDepth(1000);
      dbg.lineStyle(2, 0xff0000, 0.9);
      for (const z of this.zones) {
        const pts = (z as unknown as { polygonPoints: Phaser.Geom.Point[] }).polygonPoints;
        if (pts && pts.length >= 3) dbg.strokePoints(pts, true);
      }
    }

    // Bottom bezel — matches the bankroll bezel at the top of the screen
    // (drawn in CrapsUIScene). Sits behind the chip rack so the rack
    // visually nests inside the tray.
    const dprForBezel = (this.game.registry.get('dpr') as number | undefined) ?? 1;
    const rackBezelW = Math.min(W * 0.62, Math.round(380 * dprForBezel));
    const rackBezelH = Math.round(60 * dprForBezel);
    new Bezel({
      scene: this,
      centerX: Math.round(W / 2),
      outerY: H,
      width: rackBezelW,
      height: rackBezelH,
      face: 'up',
    });

    this.rack = new ChipRack(this, layout.chipRack.x, layout.chipRack.y, layout.chipRack.scale);

    const diceSize = layout.dice.size;
    // Half-separation between dice centers (matches the tutorial scene so a
    // shared highlight circle wraps both dice cleanly). 0.75 leaves a visible
    // gap between the dice (~0.5 of one die wide) so they read as two
    // distinct cubes instead of a fused 2×1 block.
    const diceHalfSep = Math.round(diceSize * 0.75);
    // Dice live in CrapsDiceOverlayScene (sits above UIScene) so they fly
    // over pills, history, and modals. We hold the references locally so
    // the bridge keeps using table.dice1 / table.dice2.
    const overlay = this.scene.get('CrapsDiceOverlayScene') as
      import('./DiceOverlayScene.js').DiceOverlayScene;
    const created = overlay.setupDice({
      home: { x: layout.dice.x, y: layout.dice.y, halfSep: diceHalfSep },
      diceSize,
    });
    this.dice1 = created.dice1;
    this.dice2 = created.dice2;
    // Bring the overlay scene to the top once the launch settles.
    this.time.delayedCall(0, () => overlay.scene.bringToTop());

    const sim = createSimulator({
      bounds: { left: 0, right: W, top: 0, bottom: H },
      wallRestitution: PHYSICS.wallRestitution,
      pairRestitution: PHYSICS.pairRestitution,
      diceChipRestitution: PHYSICS.diceChipRestitution,
      settleVelThreshold: PHYSICS.settleVelThreshold,
      settleAngVelThreshold: PHYSICS.settleAngVelThreshold,
      settleFrames: PHYSICS.settleFrames,
      impactSpeedThreshold: PHYSICS.impactSpeedThreshold,
      lowSpeedThreshold: PHYSICS.lowSpeedThreshold,
      lowSpeedDampingPerSec: PHYSICS.lowSpeedDampingPerSec,
      lowSpeedAngDampingPerSec: PHYSICS.lowSpeedAngDampingPerSec,
      onImpact: (e) => {
        // Dice-only — chip-scatter bounces shouldn't buzz the phone.
        if (e.bodyId.startsWith('__dice_throw')) feel.wallImpactHaptic(e.speed);
        // Every wall touch shakes the camera; intensity scales with impact
        // speed. Bypasses feel.shake() so no color flash overlays —
        // a die hitting the rail shouldn't tint the felt red.
        if (!feel.shakeEnabled()) return;
        const [duration, intensity] = e.speed >= 1500 ? [320, 0.012]
                                    : e.speed >= 600  ? [180, 0.006]
                                    :                   [ 60, 0.002];
        this.cameras.main.shake(duration, intensity);
      },
    });

    const tween: TweenAdapter = {
      tween: ({ durationMs, onUpdate, onComplete }) => {
        const t = this.tweens.addCounter({
          from: 0, to: 1,
          duration: durationMs,
          ease: GESTURE.returnEase,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onUpdate: (tw) => onUpdate((tw as any).getValue() ?? 0),
          onComplete,
        });
        return { stop: () => t.stop() };
      },
    };

    this.diceThrow = new DiceThrow({
      dice1: this.dice1, dice2: this.dice2,
      dieHalfSize: diceSize / 2,
      home: { x: layout.dice.x, y: layout.dice.y, halfSep: diceHalfSep },
      bounds: { left: 0, right: W, top: 0, bottom: H },
      sim, tween,
      rollEnabled: () => this.rollEnabled,
      setRollEnabled: (v) => this.setRollEnabled(v),
      onRollRequest: () => this.events.emit('roll:request'),
    });

    // Pointer interaction: a shared hit area covers both dice + the gap.
    const hit = this.add.rectangle(
      layout.dice.x, layout.dice.y,
      diceHalfSep * 2 + diceSize, diceSize,
      0x000000, 0,
    ).setInteractive({ useHandCursor: true });
    hit.on('pointerdown', (p: Phaser.Input.Pointer) => {
      this.diceThrow.onPointerDown(p.x, p.y, p.event.timeStamp);
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      this.diceThrow.onPointerMove(p.x, p.y, p.event.timeStamp);
    });
    this.input.on('pointerup', (p: Phaser.Input.Pointer) => {
      this.diceThrow.onPointerUp(p.x, p.y, p.event.timeStamp);
    });
    // Releasing off-canvas = throw with the dice's current velocity (which
    // is whatever the spring imparted as the player flicked off-screen).
    // Cancelling would feel wrong — a fast swipe shouldn't gently park the
    // dice back home.
    this.input.on('pointerupoutside', (p: Phaser.Input.Pointer) => {
      this.diceThrow.onPointerUp(p.x, p.y, p.event.timeStamp);
    });

    // Per-frame physics + state machine update.
    const driveStep = (_t: number, deltaMs: number) => {
      const dt = deltaMs / 1000;
      sim.step(dt);
      this.diceThrow.update(dt);
    };
    this.events.on(Phaser.Scenes.Events.UPDATE, driveStep);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.events.off(Phaser.Scenes.Events.UPDATE, driveStep);
      this.shimmerClock?.destroy();
      this.shimmerClock = undefined;
    });

    // Bump dice depth above zones/chips/rack for the throw lifecycle.
    this.dice1.setDepth(DEPTH.diceThrow);
    this.dice2.setDepth(DEPTH.diceThrow);

    this.puck = new Puck(this, layout.puck.x, layout.puck.y, layout.puck.scale);
    this.puckOffAnchor = { x: layout.puck.x, y: layout.puck.y };

    const devOn = location.search.includes('dev') || localStorage.getItem('dev') === '1';
    const shakeTestOn = location.search.includes('shake-test=true') || localStorage.getItem('shake-test') === '1';
    if (devOn || shakeTestOn) {
      (window as unknown as { __feelScene?: Phaser.Scene }).__feelScene = this;
    }
  }

  throwAndSettle(d1: number, d2: number, opts: { onSettle: () => void }): Promise<void> {
    return this.diceThrow.throwAndSettle(d1, d2, opts);
  }

  isHelpMode() {
    return this.helpMode;
  }

  setHelpMode(on: boolean) {
    this.helpMode = on;
    if (on) {
      if (!this.helpTint) {
        this.helpTint = this.add
          .rectangle(0, 0, this.scale.width, this.scale.height, 0x4a8fd4, 0.08)
          .setOrigin(0, 0)
          .setDepth(890);
      }
      if (!this.helpBanner) {
        const dpr = (this.game.registry.get('dpr') as number | undefined) ?? 1;
        // Anchored to the right of the ? pill (left x≈54*dpr, vertically
        // centred on the pill at y≈58*dpr) so it doesn't crash into the
        // history strip below.
        this.helpBanner = this.add
          .text(
            Math.round(54 * dpr),
            Math.round(58 * dpr),
            'Tap any bet to learn about it · ? to exit',
            {
              fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
              fontSize: `${Math.round(13 * dpr)}px`,
              color: '#f4d35e',
              fontStyle: 'bold',
            },
          )
          .setOrigin(0, 0.5)
          .setDepth(891);
      }
    } else {
      this.helpTint?.destroy();
      this.helpTint = undefined;
      this.helpBanner?.destroy();
      this.helpBanner = undefined;
    }
  }

  setRollEnabled(enabled: boolean) {
    this.rollEnabled = enabled;
    this.dice1?.setRollReady(enabled);
    this.dice2?.setRollReady(enabled);
  }

  /**
   * Rebuild the chip rack with only denominations the table will accept.
   * Called by the bridge once persisted rules are resolved (the registry is
   * empty during scene.create, so we open with the full set and narrow here).
   */
  refreshChipRack(tableMinCents: number, tableMaxCents: number) {
    const denoms = availableDenominations(tableMinCents, tableMaxCents);
    const dpr = (this.game.registry.get('dpr') as number | undefined) ?? 1;
    const W = this.scale.width;
    const chipScale = Math.max(1, Math.min(2, W / 520));
    const chipRackSpacing = 70 * chipScale;
    const chipRackHalfSpan = ((denoms.length - 1) * chipRackSpacing) / 2;
    const rackX = Math.round(W / 2 - chipRackHalfSpan);
    const rackY = this.rack.y;
    const prevSelected = this.rack.selectedCents;
    this.rack.destroy();
    this.rack = new ChipRack(this, rackX, rackY, chipScale, denoms);
    this.rack.selectedCents = denoms.includes(prevSelected)
      ? prevSelected
      : (denoms[0] ?? prevSelected);
    void dpr; // dpr currently unused here but keep handy for future tweaks
  }
}
